import { ChangeDetectionStrategy, Component, computed, Inject, Input, OnInit, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs';

import { PieceProductCarouselSettings, PieceProductCarouselSource } from 'extensions/core/piece-product-carousel/front/models/piece-product-carousel.model';
import { MediaLocation } from 'idt-ecommerce-api/catalog/products/medias/medias.models';
import { ProductsHelpers } from 'idt-ecommerce-api/catalog/products/products.helpers';
import { Product, ProductColor } from 'idt-ecommerce-api/catalog/products/products.models';
import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { StoreLanguage } from 'idt-ecommerce-api/stores/stores.models';
import { getStringParam } from 'render/render-shared/ui/render-product-prices/render-product-prices-utils';
import { RenderByDeviceService } from 'render/services/base-render-by-device/render-by-device.service';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { GrowthbookConfig, RenderGrowthbookConfigsService } from 'render/services/render-growthbook-configs/render-growthbook-configs.service';
import { UnsubscribeComponent } from 'shared/directives/unsubscribe.component';
import { LazyDataInterface } from 'shared/lazy-load/lazy-load.interface';
import { IdtComponent } from 'shared/models/idt-components/idt-component.models';
import { InditexWindow } from 'shared/models/inditex-window.models';
import { AssetsSharedService } from 'shared/services/assets-shared.service';
import { Link } from 'shared/services/links.model';

import { templateUrl, styleUrls } from './render-product-colors.component.paths';


const EXTERNAL_SOURCES: PieceProductCarouselSource[] = ['externalRecommendations', 'yodaRecommendedForYou', 'yodaTrends'];

@Component({
  selector: 'render-product-colors',
  templateUrl,
  styleUrls,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.--color-counter]': 'colorCounter()',
    '[style.--background-color-counter]': 'backgroundColorCounter()'
  }
})
export class RenderProductColorsComponent extends UnsubscribeComponent implements OnInit {

  @Input() public piece: IdtComponent<PieceProductCarouselSettings> | undefined;
  @Input() public product!: Product;
  @Input() public lang?: string;
  @Input() public sourceType: PieceProductCarouselSource = 'category';

  public direction: 'ltr' | 'rtl' = 'ltr';
  public visibleColors: ProductColor[] = [];
  public remainingColors: number = 0;

  public readonly colorCounter = computed(() => {
    return getStringParam(this.configs(), 'content.home-carrusel.primary-color', '#000000');
  });

  public readonly backgroundColorCounter = computed(() => {
    return `${this.colorCounter()}1A`;
  });

  private readonly configs: Signal<GrowthbookConfig[]> = toSignal(
    this.renderGrowthbookService.configurations$,
    { initialValue: [] as GrowthbookConfig[] }
  );

  constructor(
    @Inject('Window') private readonly window: InditexWindow,
    private readonly contextService: ContextService,
    private readonly renderConfigService: RenderConfigurationService,
    private readonly renderByDeviceService: RenderByDeviceService,
    private readonly assetsSharedService: AssetsSharedService,
    private readonly renderGrowthbookService: RenderGrowthbookConfigsService
  ) {
    super();
  }

  public get isMobile(): boolean {
    return this.renderByDeviceService.isMobile;
  }

  public ngOnInit(): void {
    this.calculateVisibleColors();

    this.contextService.getCurrentLanguage$().pipe(
      distinctUntilChanged(),
      filter((currentLang: StoreLanguage) => !!currentLang?.direction),
      takeUntil(this.clearSubscriptions$)
    ).subscribe((currentLang: StoreLanguage) => {
      this.direction = currentLang.direction;
    });
  }

  public getColorImageData(color: ProductColor): LazyDataInterface | undefined {
    const width = 12;
    const location: MediaLocation = 'colorcut';
    const viewStyle = '_3_1';

    const media = ProductsHelpers.firstImageForColor(color, location, width, viewStyle);
    const imageData = this.assetsSharedService.productImageData(
      media,
      width,
      this.renderConfigService.config
    );

    return imageData;
  }

  public getProductLink(color: ProductColor): Link {
    const link: Link = {
      type: 'product',
      pieceName: this.piece?.name,
      productId: this.product.id,
      ...(this.piece?.settings?.general.identifier && { identifier: this.piece.settings.general.identifier }),
      ...(this.piece?.settings?.general.enableUnifiedUrl
        && { enableUnifiedUrl: this.piece.settings.general.enableUnifiedUrl })
    };

    if (this.product.productPartNumberDetails) {
      link.productPartNumberDetails = { ...this.product.productPartNumberDetails, colorId: color.id };
    }

    if (EXTERNAL_SOURCES.includes(this.sourceType) && this.window.inditex?.iPOCRecommendationToken) {
      link.extraQueryParams = [{
        key: 'rtoken',
        value: `${this.window.inditex?.iPOCRecommendationToken}`
      }];
    }

    return link;
  }

  public trackByColors(index: number, color: ProductColor): string {
    return color.id;
  }

  private calculateVisibleColors(): void {
    if (!this.product?.colors || this.product?.colors?.length <= 1) {
      return;
    }
    this.visibleColors = this.product?.colors.length <= 2 ? this.product.colors : this.product.colors.slice(0, 1);
    this.remainingColors = this.product?.colors.length > 2 ? this.product.colors.length - 1 : 0;
  }

}
